// Header control in account builds (white on brand-dark): who is signed in on this device,
// with sign out. There is no persona switching: one person per sign-in.
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button, Sheet } from '@/components/ui'
import { PersonaSwitcher } from '@/features/demo'
import { rolesLabel, shortName, uniqueRoleCodes } from '@/features/demo/personas'
import { isAccountBuild } from '@/lib/env'
import { useMe, useSession } from '@/lib/queries'
import { signOutAccount } from '@/lib/session'

/** The one "who am I" control: the persona menu in the demo, the account menu otherwise. */
export function IdentityControl() {
  return isAccountBuild ? <AccountMenu /> : <PersonaSwitcher />
}

export function AccountMenu() {
  const session = useSession()
  const me = useMe()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)

  if (!session.account) {
    if (location.pathname === '/sign-in') return null
    return (
      <Link
        to="/sign-in"
        className="tap inline-flex items-center rounded-lg border border-white/40 px-3 text-sm font-semibold text-white hover:bg-white/10"
      >
        Sign in
      </Link>
    )
  }

  const name = me.data?.person.display_name ?? session.account.email
  const codes = me.data ? uniqueRoleCodes(me.data.credentials.map((c) => c.role_code)) : []
  const roleLine = codes.length > 0 ? codes.join(' + ') : session.account.email
  const close = () => {
    setOpen(false)
    window.setTimeout(() => trigger.current?.focus(), 0)
  }
  const signOut = async () => {
    setBusy(true)
    try {
      await signOutAccount()
      queryClient.clear()
      setOpen(false)
      navigate('/sign-in', { replace: true })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="account-menu"
        className="flex max-w-[9.5rem] items-center gap-2 rounded-lg border border-white/30 bg-white/10 px-2.5 py-1 text-left text-white hover:bg-white/20 sm:max-w-[16rem]"
      >
        <span className="min-w-0 flex-1">
          <span className="sr-only">Signed in as: </span>
          <span className="block break-words text-sm font-semibold leading-tight">{shortName(name)}</span>
          <span className="block truncate text-xs leading-tight text-white/80" title={roleLine}>
            {roleLine}
          </span>
        </span>
        <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open
        ? createPortal(
            <Sheet open title="Your account" onClose={close}>
              <div className="space-y-4 text-ink">
                <div className="rounded-lg border border-line bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Signed in as</p>
                  <p className="font-semibold">{name}</p>
                  {me.data?.person.organisation ? <p className="text-sm text-ink-soft">{me.data.person.organisation}</p> : null}
                  <p className="text-sm text-ink-soft">{session.account.email}</p>
                  <p className="text-sm text-ink-soft">{me.data ? rolesLabel(me.data.credentials.map((c) => c.role_code)) || 'No PTW licence recorded' : ''}</p>
                  <p className="mt-1 text-xs text-ink-soft">
                    Everything you sign is recorded with your name and the server time. Never let anyone else use your sign-in.
                  </p>
                </div>
                <Button variant="secondary" onClick={() => void signOut()} loading={busy} data-testid="sign-out">
                  Sign out on this device
                </Button>
              </div>
            </Sheet>,
            document.body,
          )
        : null}
    </>
  )
}
